export interface TelegramWebApp {
  initData: string;
  initDataUnsafe?: { user?: { id: number; first_name?: string; username?: string } };
  version?: string;
  colorScheme?: 'light' | 'dark';
  ready(): void;
  expand(): void;
  close(): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  BackButton?: { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void };
  HapticFeedback?: {
    impactOccurred(style: 'light' | 'medium' | 'heavy'): void;
    notificationOccurred(type: 'error' | 'success' | 'warning'): void;
  };
  showAlert?(message: string, cb?: () => void): void;
  showPopup?(params: unknown, cb?: (id: string) => void): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export const webApp = (): TelegramWebApp | undefined => window.Telegram?.WebApp;

/** True only inside a real Telegram client (the SDK also loads in a browser). */
export const inTelegram = (): boolean => Boolean(webApp()?.initData);

/**
 * Telegram clients throw `WebAppMethodUnsupported` for anything newer than the
 * running client version, and the SDK is also loaded when the page is opened in
 * a plain browser. Every optional call therefore has to be guarded.
 */
function attempt(action: (app: TelegramWebApp) => void): void {
  const app = webApp();
  if (!app) return;
  try {
    action(app);
  } catch {
    /* older client: the feature is simply unavailable */
  }
}

export function initTelegram(): void {
  attempt((app) => app.ready());
  attempt((app) => app.expand());
  attempt((app) => app.setHeaderColor?.('#ffffff'));
  attempt((app) => app.setBackgroundColor?.('#ffffff'));
}

export function haptic(type: 'success' | 'error' | 'warning'): void {
  attempt((app) => app.HapticFeedback?.notificationOccurred(type));
}

export function tap(): void {
  attempt((app) => app.HapticFeedback?.impactOccurred('light'));
}

export function closeApp(): void {
  if (inTelegram()) {
    attempt((app) => app.close());
    return;
  }
  if (window.history.length > 1) window.history.back();
  else window.location.hash = '#/';
}

export function alertUser(message: string): void {
  const app = webApp();
  if (app?.showAlert) {
    try {
      app.showAlert(message);
      return;
    } catch {
      /* client too old for showAlert: fall through to the DOM dialog */
    }
  }
  window.alert(message);
}

export async function copyText(value: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(value);
  } catch {
    const input = document.createElement('textarea');
    input.value = value;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    input.remove();
  }
}
